import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useMotionValue, useReducedMotion, useSpring, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import ArrowLeft from 'lucide-react/dist/esm/icons/arrow-left.js';
import Check from 'lucide-react/dist/esm/icons/check.js';
import Sparkles from 'lucide-react/dist/esm/icons/sparkles.js';
import './HomeHeroShowcase.css';

const workImage = (name) => `/hero_images/works_page/${name}.webp`;
const MotionLink = motion(Link);

const heroTiles = [
  {
    id: 'tall',
    images: [
      { src: workImage('hero_34'), alt: 'שני קפוצ׳ונים מודפסים בצבעי בורדו ואפור', objectPosition: '51% center' },
      { src: workImage('hero_23'), alt: 'קפוצ׳ון שחור עם הדפס צבעוני מלא', objectPosition: 'center' },
      { src: workImage('hero_17'), alt: 'קפוצ׳ון חום עם הדפס לבן סביב הרוכסן', objectPosition: 'center' },
    ],
    className: 'home-hero-proof__tile home-hero-proof__tile--tall',
    depth: 14,
  },
  {
    id: 'anchor',
    images: [
      { src: workImage('hero_18'), alt: 'הדפס סמוראי צבעוני ומפורט על חולצה שחורה', objectPosition: 'center' },
    ],
    className: 'home-hero-proof__tile home-hero-proof__tile--wide',
    depth: -10,
  },
  {
    id: 'square',
    images: [
      { src: workImage('hero_32'), alt: 'הדפס קווים לבנים מדויק על חולצה שחורה', objectPosition: 'center' },
      { src: workImage('hero_24'), alt: 'הדפס צבעוני על בגד כהה', objectPosition: 'center' },
      { src: workImage('hero_29'), alt: 'עבודת מיתוג צבעונית על חולצה', objectPosition: 'center' },
    ],
    className: 'home-hero-proof__tile home-hero-proof__tile--square',
    depth: 8,
  },
  {
    id: 'portrait',
    images: [
      { src: workImage('hero_10'), alt: 'חולצה לבנה עם הדפס טיגריס צבעוני', objectPosition: 'center 43%' },
      { src: workImage('hero_25'), alt: 'חולצה שחורה עם הדפס כחול מפורט', objectPosition: 'center' },
      { src: workImage('hero_27'), alt: 'חולצת ספורט שחורה עם הדפס ירוק ולבן', objectPosition: 'center' },
    ],
    className: 'home-hero-proof__tile home-hero-proof__tile--portrait',
    depth: -16,
  },
  {
    id: 'batch',
    images: [
      { src: workImage('hero_9'), alt: 'ערימת חולצות שחורות מודפסות בתוך קרטון', objectPosition: 'center 52%' },
      { src: workImage('hero_30'), alt: 'חולצה שחורה עם הדפס WILCO צבעוני', objectPosition: 'center 43%' },
    ],
    className: 'home-hero-proof__tile home-hero-proof__tile--batch',
    depth: 11,
  },
];

function useMediaQuery(query) {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const media = window.matchMedia(query);
    const update = () => setMatches(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, [query]);

  return matches;
}

function HeroTile({ tile, activeImage, index, smoothX, smoothY, reduceMotion, isTouchLayout }) {
  const x = useTransform(smoothX, [-1, 1], [-tile.depth, tile.depth]);
  const y = useTransform(smoothY, [-1, 1], [-tile.depth * 0.65, tile.depth * 0.65]);

  return (
    <motion.figure
      className={tile.className}
      role="img"
      aria-label={activeImage.alt}
      aria-live="off"
      data-hero-tile={tile.id}
      data-hero-image={activeImage.src}
      data-hero-rotating={tile.images.length > 1 ? 'true' : 'false'}
      style={reduceMotion || isTouchLayout ? undefined : { x, y }}
      initial={reduceMotion ? false : { opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, delay: 0.12 + index * 0.07 }}
    >
      <AnimatePresence initial={false}>
        <motion.img
          key={activeImage.src}
          src={activeImage.src}
          alt=""
          aria-hidden="true"
          draggable="false"
          style={{ objectPosition: activeImage.objectPosition }}
          initial={reduceMotion ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: reduceMotion ? 0 : 0.7, ease: 'easeInOut' }}
        />
      </AnimatePresence>
    </motion.figure>
  );
}

const HomeHeroShowcase = () => {
  const reduceMotion = useReducedMotion();
  const isTouchLayout = useMediaQuery('(max-width: 900px)');
  const isMobileLayout = useMediaQuery('(max-width: 720px)');
  const heroRef = useRef(null);
  const lastRotatedTileRef = useRef(-1);
  const [activeImageIndexes, setActiveImageIndexes] = useState(() => heroTiles.map(() => 0));
  const [rotationRetryNonce, setRotationRetryNonce] = useState(0);
  const [isHeroVisible, setIsHeroVisible] = useState(true);
  const [isPageVisible, setIsPageVisible] = useState(() => !document.hidden);
  const [hasHeroInteraction, setHasHeroInteraction] = useState(false);
  const pointerX = useMotionValue(0);
  const pointerY = useMotionValue(0);
  const smoothX = useSpring(pointerX, { stiffness: 90, damping: 22 });
  const smoothY = useSpring(pointerY, { stiffness: 90, damping: 22 });

  useEffect(() => {
    if (!heroRef.current || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(([entry]) => setIsHeroVisible(entry.isIntersecting), { threshold: 0.15 });
    observer.observe(heroRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const updateVisibility = () => setIsPageVisible(!document.hidden);
    document.addEventListener('visibilitychange', updateVisibility);
    return () => document.removeEventListener('visibilitychange', updateVisibility);
  }, []);

  useEffect(() => {
    if (hasHeroInteraction) return undefined;
    const enableRotation = () => setHasHeroInteraction(true);
    const listenerOptions = { once: true, passive: true };

    window.addEventListener('pointerdown', enableRotation, listenerOptions);
    window.addEventListener('scroll', enableRotation, listenerOptions);
    window.addEventListener('keydown', enableRotation, { once: true });
    return () => {
      window.removeEventListener('pointerdown', enableRotation);
      window.removeEventListener('scroll', enableRotation);
      window.removeEventListener('keydown', enableRotation);
    };
  }, [hasHeroInteraction]);

  useEffect(() => {
    if (
      reduceMotion ||
      !hasHeroInteraction ||
      !isHeroVisible ||
      !isPageVisible
    ) {
      return undefined;
    }
    const rotatableTiles = heroTiles
      .map((tile, tileIndex) => (tile.images.length > 1 ? tileIndex : -1))
      .filter((tileIndex) => tileIndex >= 0);
    if (rotatableTiles.length === 0) return undefined;

    let cancelled = false;
    let pendingImage = null;
    const timer = window.setTimeout(() => {
      const availableTiles = rotatableTiles.filter((tileIndex) => tileIndex !== lastRotatedTileRef.current);
      const candidates = availableTiles.length > 0 ? availableTiles : rotatableTiles;
      const tileIndex = candidates[Math.floor(Math.random() * candidates.length)];
      const tile = heroTiles[tileIndex];
      const currentImageIndex = activeImageIndexes[tileIndex] ?? 0;
      const nextImageIndex = (currentImageIndex + 1) % tile.images.length;
      const nextImage = tile.images[nextImageIndex];
      pendingImage = new Image();

      const commitRotation = () => {
        if (cancelled) return;
        setActiveImageIndexes((currentIndexes) => {
          if ((currentIndexes[tileIndex] ?? 0) !== currentImageIndex) return currentIndexes;
          const updatedIndexes = [...currentIndexes];
          updatedIndexes[tileIndex] = nextImageIndex;
          lastRotatedTileRef.current = tileIndex;
          return updatedIndexes;
        });
      };
      const retryAfterFailure = () => {
        if (!cancelled) setRotationRetryNonce((current) => current + 1);
      };
      pendingImage.onload = () => {
        if (typeof pendingImage.decode === 'function') pendingImage.decode().catch(() => undefined).finally(commitRotation);
        else commitRotation();
      };
      pendingImage.onerror = retryAfterFailure;
      pendingImage.src = nextImage.src;
      if (pendingImage.complete && pendingImage.naturalWidth > 0) pendingImage.onload();
    }, isMobileLayout ? 7000 : 5000);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      if (pendingImage) {
        pendingImage.onload = null;
        pendingImage.onerror = null;
      }
    };
  }, [
    activeImageIndexes,
    hasHeroInteraction,
    isHeroVisible,
    isMobileLayout,
    isPageVisible,
    reduceMotion,
    rotationRetryNonce,
  ]);

  const handlePointerMove = (event) => {
    if (reduceMotion || isTouchLayout) return;
    const rect = event.currentTarget.getBoundingClientRect();
    pointerX.set(((event.clientX - rect.left) / rect.width - 0.5) * 2);
    pointerY.set(((event.clientY - rect.top) / rect.height - 0.5) * 2);
  };

  const handlePointerLeave = () => {
    pointerX.set(0);
    pointerY.set(0);
  };

  return (
    <section className="home-hero" id="top" ref={heroRef}>
      <div className="home-hero__content">
        <motion.div
          className="home-hero__copy"
          initial={reduceMotion ? false : { opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
        >
          <span className="home-hero__eyebrow"><Sparkles size={16} /> בחירת מוצר ומיתוג באתר</span>
          <h1>בוחרים ביגוד, צבעים, מידות ואזורי מיתוג — אונליין</h1>
          <p>מעלים עיצוב, בודקים את פרטי ההזמנה וממשיכים לסל ולתשלום באתר</p>
          <div className="home-hero__actions">
            <Link className="home-hero__cta" to="/catalog">התחילו הזמנה <ArrowLeft size={20} /></Link>
          </div>
        </motion.div>

        <MotionLink
          className="home-hero-proof"
          to="/works"
          aria-label="לצפייה בכל העבודות של Printeam"
          onMouseMove={handlePointerMove}
          onMouseLeave={handlePointerLeave}
          initial={reduceMotion ? false : { opacity: 0, scale: 0.985 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
        >
          {heroTiles.map((tile, index) => (
            <HeroTile
              key={tile.id}
              tile={tile}
              activeImage={tile.images[activeImageIndexes[index] ?? 0]}
              index={index}
              smoothX={smoothX}
              smoothY={smoothY}
              reduceMotion={reduceMotion}
              isTouchLayout={isTouchLayout}
            />
          ))}
          <div className="home-hero-proof__label"><Check size={16} /> דוגמאות מתוך עמוד העבודות</div>
        </MotionLink>
      </div>
    </section>
  );
};

export default HomeHeroShowcase;
